package com.upcominds.expedite.features.challenges

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.ApiException
import com.upcominds.expedite.data.challenges.ChallengesRepository
import com.upcominds.expedite.data.documents.DocumentPicker
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.dto.SavingsBillUploadDTO
import com.upcominds.expedite.dto.SavingsChallengeProgressDTO
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class SavingsChallengeUiState(
    val loading: Boolean = false,
    val uploading: Boolean = false,
    val deletingBillId: String? = null,
    val error: String? = null,
    val progress: SavingsChallengeProgressDTO? = null,
    val uploads: List<SavingsBillUploadDTO> = emptyList()
)

class SavingsChallengeViewModel(
    private val challengesRepository: ChallengesRepository,
    private val documentPicker: DocumentPicker
) : ViewModel() {

    private val _uiState = MutableStateFlow(SavingsChallengeUiState())
    val uiState: StateFlow<SavingsChallengeUiState> = _uiState.asStateFlow()

    fun load(challengeId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val progress = challengesRepository.getSavingsProgress(challengeId)
                val uploads = challengesRepository.getSavingsBills(challengeId)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    progress = progress,
                    uploads = uploads
                )
            } catch (e: Exception) {
                logMobileError("SavingsChallengeViewModel.load", e, "Failed to load savings challenge $challengeId")
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = toUserMessage(e)
                )
            }
        }
    }

    fun pickAndUpload(challengeId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(uploading = true, error = null)
            try {
                val file = documentPicker.pickPdf()
                if (file == null) {
                    _uiState.value = _uiState.value.copy(uploading = false)
                    return@launch
                }

                val result = challengesRepository.uploadSavingsBill(challengeId, file)
                val updatedUploads = (_uiState.value.uploads + result.upload)
                    .sortedByDescending { it.uploadedAt }
                _uiState.value = _uiState.value.copy(
                    uploading = false,
                    deletingBillId = null,
                    progress = result.progress,
                    uploads = updatedUploads
                )
            } catch (e: Exception) {
                logMobileError("SavingsChallengeViewModel.pickAndUpload", e, "Failed to upload savings bill for challenge $challengeId")
                _uiState.value = _uiState.value.copy(
                    uploading = false,
                    deletingBillId = null,
                    error = toUserMessage(e)
                )
            }
        }
    }

    fun deleteBill(challengeId: String, billId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(deletingBillId = billId, error = null)
            try {
                val progress = challengesRepository.deleteSavingsBill(challengeId, billId)
                val uploads = challengesRepository.getSavingsBills(challengeId)
                _uiState.value = _uiState.value.copy(
                    deletingBillId = null,
                    progress = progress,
                    uploads = uploads.sortedByDescending { it.uploadedAt }
                )
            } catch (e: Exception) {
                logMobileError("SavingsChallengeViewModel.deleteBill", e, "Failed to delete savings bill $billId")
                _uiState.value = _uiState.value.copy(
                    deletingBillId = null,
                    error = toUserMessage(e)
                )
            }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    private fun toUserMessage(error: Exception): String {
        val apiError = error as? ApiException ?: return error.message ?: "Something went wrong. Please try again."
        return when (apiError.statusCode) {
            400 -> apiError.message.ifBlank { "The uploaded file is not valid. Please upload an energy bill PDF." }
            401 -> "Your session has expired. Please sign in again."
            404 -> "Bill not found."
            409 -> apiError.message.ifBlank { "This bill could not be accepted for the challenge." }
            502, 503, 504 -> "Bill validation service is unavailable right now. Please try again in a bit."
            else -> apiError.message.ifBlank { "Upload failed. Please try again." }
        }
    }
}
